import { useRef, memo } from 'react';
import { cn } from '@/lib/utils';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Link } from 'react-router-dom';
import { getCountryFlag } from '@/lib/flagUtils';
import { LocationInfoPopover } from '@/components/locations/LocationInfoPopover';
import { Badge } from '@/components/ui/badge';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { RESPONSIVE, TYPOGRAPHY } from '@/design-system';
import { routeTo } from '@/lib/routeHelpers';
import { useLocale } from '@/contexts/LocaleContext';

interface LocationStats {
  location_id: string;
  name: string;
  short_name?: string;
  native_name?: string;
  iso_alpha2: string;
  slug: string;
  native_slug?: string;
  coordinates?: string;
  total_experts: number;
  listed_profiles: number;
  verified_experts: number;
}

interface VirtualizedLocationTableProps {
  locations: LocationStats[];
  startIndex?: number;
  totalExperts?: number;
  totalLocations?: number;
}

const VirtualizedLocationTableInner = ({ 
  locations,
  startIndex = 0,
  totalExperts,
  totalLocations
}: VirtualizedLocationTableProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const { lComponents } = useLocalizations();
  const { formatNumber } = useLocale();
  const ROW_HEIGHT = 64;

  const virtualizer = useWindowVirtualizer({
    count: locations.length,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div ref={listRef} className="border rounded-lg">
      {/* Sticky Header */}
      <div className="sticky top-16 z-10 bg-card border-b">
        <div className={`grid grid-cols-[40px_1fr_60px] sm:grid-cols-[60px_1fr_100px] ${RESPONSIVE.gapResponsiveWide} px-2 sm:px-4 py-3 text-sm font-medium text-muted-foreground`}>
          <div><LComponents id="locations.table.rank" defaultValue='Rank' /></div>
          <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
            <LComponents id="locations.table.locations" defaultValue='Locations' />
            {totalLocations !== undefined && (
              <Badge variant="outline">{formatNumber(totalLocations)}</Badge>
            )}
          </div>
          <div className="text-right flex items-center justify-end gap-2">
            <LComponents id="locations.table.experts" defaultValue='Experts' />
            {totalExperts !== undefined && (
              <Badge variant="outline">{formatNumber(totalExperts)}</Badge>
            )}
          </div>
        </div>
      </div>
      {/* Virtual Rows Container */}
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const location = locations[virtualRow.index];
          const globalRank = startIndex + virtualRow.index + 1;
          // Use short name if available, otherwise translated name
          const displayName = location.short_name || location.name;
          const nativeName = location.native_name;
          // Show native name if it exists and is different from the display name
          const showNative = nativeName && nativeName !== displayName;

          return (
            <div
              key={location.location_id}
              className="absolute top-0 left-0 w-full border-b hover:bg-muted/50 transition-colors"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <div className={`grid grid-cols-[40px_1fr_60px] sm:grid-cols-[60px_1fr_100px] ${RESPONSIVE.gapResponsiveWide} px-2 sm:px-4 h-full items-center`}>
                {/* Rank */}
                <div className="font-medium text-muted-foreground">
                  {globalRank}
                </div>
                
                {/* Location */}
                <div className={cn(RESPONSIVE.flexRowCenterGap3, 'min-w-0')}>
                  <LocationInfoPopover
                    isoCode={location.iso_alpha2}
                    name={displayName}
                    nativeName={nativeName}
                    locationId={location.location_id}
                    nativeSlug={location.native_slug || location.slug}
                    expertCount={location.total_experts || 0}
                  >
                    <button className="text-2xl cursor-pointer hover:scale-110 transition-transform flex-shrink-0">
                      {getCountryFlag(location.iso_alpha2)}
                    </button>
                  </LocationInfoPopover>
                  <Link 
                    to={routeTo.locationPage(location.native_slug || location.slug)} 
                    className="hover:text-primary transition-colors min-w-0"
                  >
                    <div className="font-semibold truncate">
                      {displayName}
                      {showNative && (
                        <span className="text-muted-foreground font-normal"> | {nativeName}</span>
                      )}
                    </div>
                    <div className={cn(TYPOGRAPHY.cardDescription)}>{location.iso_alpha2}</div>
                  </Link>
                </div>
                
                {/* Expert Count */}
                <div className="text-right">
                  <span className="font-semibold text-primary">
                    {formatNumber(location.total_experts || 0)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VirtualizedLocationTable = memo(VirtualizedLocationTableInner);
